import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../constants';
import { initializeAuth } from '../utils/auth';
import { authenticatedFetch } from '../utils/api';
import { User } from '../types/auth';

interface AuthContextType {
  isLoggedIn: boolean;
  isInitialized: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;
  loginWithCredentials: (email: string, password: string) => Promise<{success: boolean, error?: string}>;
  checkSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const navigate = useNavigate();

  // Enhanced session check - try cookies first, then JWT token from localStorage
  const checkSession = async (): Promise<boolean> => {
    // Prevent multiple simultaneous calls
    if (isCheckingSession) {
      return isLoggedIn;
    }
    
    setIsCheckingSession(true);
    try {
      // Use authenticatedFetch which handles cookie+JWT fallback automatically
      const response = await authenticatedFetch(API_URLS.AUTH.CHECK_SESSION, {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsLoggedIn(true);
        return true;
      } else {
        setUser(null);
        setIsLoggedIn(false);
        return false;
      }
    } catch (error) {
      setUser(null);
      setIsLoggedIn(false);
      return false;
    } finally {
      setIsCheckingSession(false);
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const isAuthenticated = await checkSession();
      setIsInitialized(true);
      // If user is authenticated and we're on auth pages (except verification pages), redirect to dashboard
      if (isAuthenticated && window.location.pathname.startsWith('/auth') && 
          !window.location.pathname.includes('/verify') &&
          !window.location.pathname.includes('/callback')) {
        navigate('/');
      }
    };
    
    initAuth();
    initializeAuth(navigate);
  }, [navigate]);

  // Separate effect for periodic session checks - only when logged in
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isLoggedIn) {
      // Only check session periodically when user is logged in
      interval = setInterval(checkSession, 15 * 60 * 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoggedIn]);

  // Helper function to handle post-login redirection
  const handlePostLoginRedirection = () => {
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      localStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath);
    } else {
      navigate('/');
    }
  };

  // Cookie-based login method with route persistence
  const loginWithCredentials = async (email: string, password: string): Promise<{success: boolean, error?: string}> => {
    try {
      const response = await fetch(API_URLS.AUTH.LOGIN, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsLoggedIn(true);
        
        // Save JWT token to localStorage for Firefox fallback
        if (data.token) {
          localStorage.setItem('jwt_token', data.token);
        }
        
        setTimeout(() => {
          handlePostLoginRedirection();
        }, 800);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.message || 'Login failed' };
      }
    } catch {
      return { success: false, error: 'Network error occurred' };
    }
  };

  // Clear all authentication cookies and tokens
  const clearAllAuthData = () => {
    // Clear localStorage
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('redirectAfterLogin');
    localStorage.removeItem('sso_return_url');
    
    // Clear all MSAL tokens
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('msal')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear all possible cookie variations
    const cookieNames = ['auth_token', 'jwt', 'session_token', 'access_token'];
    const domains = ['', '.talavera.ai', '.promptshelf-backend-prod-4yjivosryq-uc.a.run.app', 'localhost'];
    const paths = ['/', '/api', '/auth'];
    
    cookieNames.forEach(cookieName => {
      domains.forEach(domain => {
        paths.forEach(path => {
          // Clear with domain
          if (domain) {
            document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${domain}; secure; samesite=none`;
          }
          // Clear without domain
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; secure; samesite=none`;
          document.cookie = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
        });
      });
    });
  };

  // Logout only by explicit user action
  const logout = async () => {
    try {
      await fetch(API_URLS.AUTH.LOGOUT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch {
      // Ignore logout errors - user will be logged out locally regardless
    }
    finally {
      clearAllAuthData();
      setIsLoggedIn(false);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isLoggedIn, 
      isInitialized, 
      user,
      setUser, 
      logout,
      loginWithCredentials,
      checkSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};